/**
 * Organization vs personal groups in the groups list (approved ListBadges
 * design): the badge on the name line, and the «الكل / <org> / شخصية» filter
 * pills. Pure, so the rules are tested without the screen.
 *
 * Ported from namat-mobile src/lib/groups/groupKind.ts (same rules, same
 * tests); isGroupVisibleInOrg mirrors mobile queries/groups.ts.
 */

export type GroupKind = 'org' | 'personal';

export interface KindGroup {
  id: string;
  organization?: string | null;
}

/**
 * The member's groups list scope: the ACTIVE organization's groups plus
 * personal (org-less) groups. Other organizations' groups are hidden. With no
 * active organization every group is in scope (membership filters it later).
 */
export function isGroupVisibleInOrg(
  group: { id?: string; organization?: string | null },
  organizationId?: string | null,
): boolean {
  return !organizationId || !group.organization || group.organization === organizationId;
}

/** A group with an organization field is an org group; otherwise personal. */
export function groupKind(group: KindGroup): GroupKind {
  return group.organization ? 'org' : 'personal';
}

export interface OrgNameSource {
  id: string;
  name?: string;
  nameAr?: string;
  nameEn?: string;
}

/** Same choice as the org switcher (OrgSwitcherCard): Arabic name in RTL. */
export function orgDisplayName(org: OrgNameSource | undefined, isRTL: boolean): string {
  if (!org) return '';
  const name = isRTL ? org.nameAr || org.name || org.nameEn : org.nameEn || org.name || org.nameAr;
  return (name || '').trim();
}

export interface GroupBadge {
  kind: GroupKind;
  /** Org display name, or '' for personal (the screen shows «شخصية»). */
  orgName: string;
}

/** Badge for one list row. An org whose name hasn't resolved yet shows no text. */
export function groupBadge(group: KindGroup, orgNames: Record<string, string>): GroupBadge {
  if (groupKind(group) === 'personal') return { kind: 'personal', orgName: '' };
  return { kind: 'org', orgName: orgNames[group.organization as string] || '' };
}

/** Filter pill key: 'all', 'personal', or `org:<orgId>`. */
export type GroupFilterKey = 'all' | 'personal' | `org:${string}`;

export interface GroupFilterOption {
  key: GroupFilterKey;
  /** Org id for an org pill. */
  orgId?: string;
}

/**
 * Pills shown only when the list has both org and personal groups: «الكل»,
 * then one per organization in first-seen order, then «شخصية». Empty
 * otherwise (no filter bar).
 */
export function groupFilterOptions(groups: ReadonlyArray<KindGroup>): GroupFilterOption[] {
  const orgIds: string[] = [];
  let hasPersonal = false;
  for (const g of groups) {
    if (g.organization) {
      if (!orgIds.includes(g.organization)) orgIds.push(g.organization);
    } else {
      hasPersonal = true;
    }
  }
  if (!hasPersonal || orgIds.length === 0) return [];
  return [
    { key: 'all' },
    ...orgIds.map((orgId) => ({ key: `org:${orgId}` as GroupFilterKey, orgId })),
    { key: 'personal' },
  ];
}

/** Groups matching a pill. An unknown/stale key falls back to all groups. */
export function filterGroupsByKind<G extends KindGroup>(groups: ReadonlyArray<G>, key: GroupFilterKey): G[] {
  if (key === 'personal') return groups.filter((g) => !g.organization);
  if (key.startsWith('org:')) {
    const orgId = key.slice(4);
    return groups.filter((g) => g.organization === orgId);
  }
  return [...groups];
}

/** The selected pill, reset to 'all' when it is no longer offered. */
export function effectiveFilterKey(key: GroupFilterKey, options: ReadonlyArray<GroupFilterOption>): GroupFilterKey {
  return options.some((o) => o.key === key) ? key : 'all';
}

/** The group's photo (supportGroups.photoURL) when set, for the list avatar. */
export function groupPhotoUri(group: { photoURL?: string | null } | null | undefined): string | null {
  const url = group?.photoURL;
  return typeof url === 'string' && url.trim().length > 0 ? url : null;
}
